import { useGetUtrvVariationsQuery } from '@api/utrv';
import { VariationWarningMessage } from '@features/survey/question/variation/VariationWarningMessage';
import { getUtrvVariationWarnings } from '@utils/survey/input';
import { hasOverriddenVariations } from '@features/question-configuration';
import { InitiativeUniversalTracker } from '@g17eco/types/initiativeUniversalTracker';
import { skipToken } from '@reduxjs/toolkit/query';
import { createContext, useCallback, useContext } from 'react';
import { InputColumn } from '@g17eco/types/questionInterfaces';
import { UniversalTrackerBase } from '../../../../../types/universalTracker';

interface ContextProps {
  getColumnVariationWarningMessage: (valueListCode: string) => React.ReactNode;
}

const MultiRowTableContext = createContext<ContextProps>({
  getColumnVariationWarningMessage: () => null,
});

export const useMultiRowTableContext = () => {
  return useContext(MultiRowTableContext);
};

interface Props {
  children: JSX.Element;
  utr: UniversalTrackerBase;
  aggregatedTableData?: InputColumn[][];
  utrvId?: string;
  initiativeUtr?: InitiativeUniversalTracker;
}

export const MultiRowTableProvider = ({ children, aggregatedTableData, utr, initiativeUtr, utrvId }: Props) => {
  const hasVariations = initiativeUtr && hasOverriddenVariations(initiativeUtr);
  const { data: utrvVariations } = useGetUtrvVariationsQuery(hasVariations && utrvId ? { utrvId } : skipToken);

  const getColumnVariationWarningMessage = useCallback(
    (valueListCode: string) => {
      const variationWarnings = getUtrvVariationWarnings({
        utr,
        utrvVariations,
        currentInputData: {
          aggregatedTableData,
          table: { editRowId: -1, rows: [] },
          valueData: { data: {} },
        },
      });

      const variationWarning = variationWarnings.find((warning) => warning.valueListCode === valueListCode);
      if (!variationWarning) {
        return null;
      }
      return <VariationWarningMessage variationWarning={variationWarning} />;
    },
    [aggregatedTableData, utr, utrvVariations],
  );

  return (
    <MultiRowTableContext.Provider value={{ getColumnVariationWarningMessage }}>
      {children}
    </MultiRowTableContext.Provider>
  );
};
